import { useCallback, useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Zap } from 'lucide-react';
import { QuickActionTile } from '@/components/ui/QuickActionTile';
import { actionsFor, shortcutLabel, useQuickActionShortcuts, type QuickAction } from './actions';

/**
 * The topbar ⚡ button (QuickActions_Options.html, option B): a 3 x 3 grid of
 * the same tinted tiles as the Dashboard card, from every screen. Alt+Q opens
 * and closes it; Alt+1..9 run an action directly whether or not it is open.
 * Renders nothing for a role with no quick actions.
 */
export function QuickActionsMenu({ modules, onNavigate }: { modules: readonly string[] | undefined; onNavigate: (mod: string) => void }) {
  const [open, setOpen] = useState(false);
  const actions = useMemo(() => actionsFor(modules), [modules]);
  const run = useCallback((a: QuickAction) => { setOpen(false); onNavigate(a.mod); }, [onNavigate]);
  const toggle = useCallback(() => setOpen(o => !o), []);
  useQuickActionShortcuts(actions, run, toggle);
  if (!actions.length) return null;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="Quick actions"
          title={`Quick actions (${shortcutLabel('Q')})`}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-control-line bg-card text-teal transition-colors hover:bg-muted/60 data-[state=open]:border-teal data-[state=open]:ring-[3px] data-[state=open]:ring-teal/20"
        >
          <Zap className="h-[18px] w-[18px]" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className="z-[150] w-[340px] rounded-xl border border-control-line bg-card p-3 shadow-[0_14px_34px_rgba(15,27,45,0.22)]"
        >
          <div className="mb-2 flex items-center justify-between px-0.5">
            <span className="text-[11px] font-bold uppercase tracking-[.03em] text-ink">Quick actions</span>
            <span className="text-[10.5px] text-ink-muted">{shortcutLabel('Q')}</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {actions.map(a => {
              const Icon = a.icon;
              return (
                <div key={a.id} className="relative">
                  <QuickActionTile label={a.label} tone={a.tone} onClick={() => run(a)} icon={<Icon className="h-5 w-5" />} className="w-full" />
                  <span aria-hidden className="pointer-events-none absolute right-1.5 top-1 text-[9px] font-semibold opacity-70">{shortcutLabel(a.shortcut)}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-2.5 flex justify-between border-t border-line pt-2 text-[10.5px] text-ink-muted">
            <span>Available on every screen</span>
            <span>Esc to close</span>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
